import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import api from '../api/client';
import StaffWelcomeTour, { FIRST_TIME_TOUR_ROLES, hasSeenTour } from './StaffWelcomeTour';

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes

const getUser = () => {
  try { return JSON.parse(localStorage.getItem('portalUser') || 'null'); } catch { return null; }
};

// When the stored login token expires (ms since epoch, from its `exp`
// claim), or null if it can't be read. Logins last 12h, so the layout signs
// people out at that point instead of leaving every page failing with 401s.
const tokenExpiresAt = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : null;
  } catch { return null; }
};

const NAV_BASE = [
  { to: '/dashboard', label: 'Dashboard', exact: true },
  { to: '/applications', label: 'Applications' },
  { to: '/scorecard', label: 'Scorecard' },
];

const NAV_ADMIN_ONLY = [
  { to: '/users', label: 'Users' },
  { to: '/archived-candidates', label: 'Archived' },
];

const TYPE_ICON = {
  document_uploaded: '📄',
  test_submitted: '📝',
  status_changed: '🔄',
  document_signed: '✍️',
  popia_consent_given: '🛡️',
  assignment: '📌',
};

export default function AdminLayout() {
  const navigate = useNavigate();
  const inactivityTimer = useRef(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalUser');
    navigate('/login');
  }, [navigate]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_TIMEOUT);
  }, [handleLogout]);

  // Auth check + inactivity timer
  useEffect(() => {
    const token = localStorage.getItem('portalToken');
    if (!token) {
      navigate('/login');
      return;
    }
    const expiresAt = tokenExpiresAt(token);
    if (expiresAt && expiresAt <= Date.now()) {
      handleLogout();
      return;
    }
    const expiryTimer = expiresAt ? setTimeout(handleLogout, expiresAt - Date.now()) : null;
    resetInactivityTimer();
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => document.addEventListener(e, resetInactivityTimer));
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (expiryTimer) clearTimeout(expiryTimer);
      events.forEach(e => document.removeEventListener(e, resetInactivityTimer));
    };
  }, [navigate, resetInactivityTimer, handleLogout]);
  // First-sign-in walkthrough — opens once for RMs / PMs / Unit Managers;
  // anyone can reopen it from "How it works" in the top bar.
  const [showTour, setShowTour] = useState(() => {
    const u = getUser();
    return !!u && FIRST_TIME_TOUR_ROLES.includes(u.role) && !hasSeenTour(u);
  });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  async function fetchNotifications() {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silently fail — user may not be logged in yet
    }
  }

  useEffect(() => {
    // Only fetch notifications if user has a token
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleBellClick() {
    const opening = !open;
    setOpen(opening);
    if (opening && unreadCount > 0) {
      try {
        await api.put('/notifications/read-all');
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      } catch {
        // ignore
      }
    }
  }

  function formatTime(iso) {
    const date = new Date(iso);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* ── top bar ── */}
      <header style={{
        background: 'var(--bg2)',
        borderBottom: '1px solid var(--border)',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        height: 56,
        gap: 32,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        {/* logo / wordmark */}
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <img src="/logo.svg" alt="Guud Vision" style={{
            width: 32,
            height: 32,
            objectFit: 'contain',
          }} />
          <span style={{
            fontFamily: 'var(--font-display)',
            fontSize: 17,
            color: 'var(--text)',
            letterSpacing: '0.01em',
          }}>Guud Vision</span>
        </button>

        {/* nav links */}
        <nav style={{ display: 'flex', gap: 4, flex: 1 }}>
          {/* Portal Home — external link back to Spectacles */}
          <a
            href="https://dashboard.guudvision.health/portal"
            style={{
              padding: '6px 14px',
              borderRadius: 'var(--radius-sm)',
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--text2)',
              background: 'transparent',
              border: '1px solid transparent',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-bg)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.background = 'transparent'; }}
          >
            ⌂ Portal Home
          </a>
          {[...NAV_BASE, ...(getUser()?.role === 'admin' ? NAV_ADMIN_ONLY : [])].map(({ to, label, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              style={({ isActive }) => ({
                padding: '6px 14px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 14,
                fontWeight: 500,
                color: isActive ? 'var(--accent)' : 'var(--text2)',
                background: isActive ? 'var(--accent-bg)' : 'transparent',
                border: isActive ? '1px solid rgba(200,169,110,0.2)' : '1px solid transparent',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              })}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {/* reopen the first-time walkthrough */}
        <button
          type="button"
          onClick={() => setShowTour(true)}
          title="How the Talent Pool works"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '6px 10px',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text2)',
            fontSize: 13,
            fontFamily: 'var(--font)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          ? How it works
        </button>

        {/* notification bell */}
        <div ref={dropdownRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            type="button"
            onClick={handleBellClick}
            title="Notifications"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              width: 36,
              height: 36,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text2)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute',
                top: 4,
                right: 4,
                background: '#e53e3e',
                color: '#fff',
                borderRadius: '50%',
                width: 16,
                height: 16,
                fontSize: 10,
                fontWeight: 700,
                display: 'grid',
                placeItems: 'center',
                lineHeight: 1,
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {open && (
            <div style={{
              position: 'absolute',
              top: 44,
              right: 0,
              width: 320,
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              zIndex: 200,
              overflow: 'hidden',
            }}>
              <div style={{
                padding: '10px 14px',
                borderBottom: '1px solid var(--border)',
                fontWeight: 600,
                fontSize: 13,
                color: 'var(--text)',
              }}>
                Notifications
              </div>

              {notifications.length === 0 ? (
                <div style={{ padding: '20px 14px', color: 'var(--text2)', fontSize: 13, textAlign: 'center' }}>
                  No notifications yet
                </div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 360, overflowY: 'auto' }}>
                  {notifications.map(n => (
                    <li key={n.id} style={{
                      padding: '10px 14px',
                      borderBottom: '1px solid var(--border)',
                      background: n.is_read ? 'transparent' : 'rgba(200,169,110,0.06)',
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                    }}>
                      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                        {TYPE_ICON[n.type] || '🔔'}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4 }}>
                          {n.message}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                          {formatTime(n.created_at)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* logout button */}
        <button
          type="button"
          onClick={handleLogout}
          title="Sign out"
          style={{
            background: 'none',
            border: '1px solid var(--border)',
            cursor: 'pointer',
            padding: '6px 14px',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text2)',
            fontSize: 13,
            fontFamily: 'var(--font)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign out
        </button>
      </header>

      {/* ── page content ── */}
      <main style={{ flex: 1, padding: '28px 24px', maxWidth: 1100, width: '100%', margin: '0 auto' }}>
        <Outlet />
      </main>

      {showTour && <StaffWelcomeTour user={getUser()} onClose={() => setShowTour(false)} />}
    </div>
  );
}
